import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import UserTable from './components/UserTable/UserTable';
import ClientDetails from './components/ClientDetails/ClientDetails';
import LoginForm from './components/LoginForm/LoginForm';
import HomePage from './components/Home/Home';
import './App.scss';
import OrdersDashboard from './components/ViewOrders/ViewOrders';
import BusinessCalendar from './components/BusinessCalendar/BusinessCalendar';
import BusinessContext from './components/BusinessContext/BusinessContext';
import WebBusinessPage from './components/WebBusinessPage/WebBusinessPage';
import { useUser } from './context/UserContext';
import ContactForm from './components/ContactForm/ContactForm';


function App() {
  const [theme, setTheme] = useState('light');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { user, setUser } = useUser();

  // Check for existing token on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('userData');

    if (token && userData) {
      try {
        const parsedUserData = JSON.parse(userData);
        setUser(parsedUserData);
        setIsLoggedIn(true);
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('userData');
      }
    }
    setIsLoading(false);
  }, []);

  const handleLoginSuccess = (userData) => {
    setUser(userData);
    setIsLoggedIn(true);
    localStorage.setItem('userData', JSON.stringify(userData));
    window.location.href = `/web/${userData.business_profile.slug}`;
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
    localStorage.removeItem('currentBusinessId');
    setIsLoggedIn(false);
    setUser(null);
    setShowLogoutModal(false);
    window.location.href = '/';
  };

  const handleLogoutClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowLogoutModal(true);
  };

  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  // Protected Route component
  const ProtectedRoute = ({ children }) => {
    if (!isLoggedIn) {
      return <Navigate to="/login" replace />;
    }
    return children;
  };

  // Public Layout Component
  const PublicLayout = ({ children }) => {
    return (
      <>
        {/* Public Navbar */}
        <nav className="navbar navbar-expand-lg navbar-dark bg-primary shadow">
          <div className="container-fluid">
            <Link className="navbar-brand" to="/">
              <i className="fas fa-users-cog me-2"></i>
              AI Assistant
            </Link>

            <button className="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarNav">
              <span className="navbar-toggler-icon"></span>
            </button>

            <div className="collapse navbar-collapse" id="navbarNav">
              <ul className="navbar-nav me-auto">
                {/* <li className="nav-item">
                  <Link className="nav-link" to="/">Home</Link>
                </li>
                <li className="nav-item">
                  <Link className="nav-link" to="/features">Features</Link>
                </li>
                <li className="nav-item">
                  <Link className="nav-link" to="/pricing">Pricing</Link>
                </li> */}
                <li className="nav-item">
                  <Link className="nav-link" to="/contact">Contact</Link>
                </li>
              </ul>

              <div className="d-flex align-items-center">
                {/* <button
                  className="btn btn-outline-light me-3"
                  onClick={toggleTheme}
                  title={`Switch to ${theme === 'light' ? 'dark' : 'light'} theme`}
                >
                  <i className={`fas fa-${theme === 'light' ? 'moon' : 'sun'}`}></i>
                </button> */}

                {!isLoggedIn ? (
                  <>
                    <Link to="/login" className="btn btn-outline-light me-2">
                      <i className="fas fa-sign-in-alt me-1"></i>
                      <span className="poppin">Login</span>
                    </Link>
                    <Link to="/register" className="btn btn-light">
                      <i className="fas fa-user-plus me-1"></i>
                      <span className="poppin">Sign Up</span>
                    </Link>
                  </>
                ) : (
                  <Link to={`/web/${user.business_profile.slug}`} className="btn-glass-primary no-underline">
                    <i className="fas fa-tachometer-alt me-1"></i>
                    <span className="poppin no-underline" style={{ textDecoration: 'none' }}>Dashboard</span>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </nav>

        {/* Main Content */}
        <main>
          {children}
        </main>

        {/* Public Footer */}
        <footer className="footer mt-auto py-4 bg-light border-top">
          <div className="container">
            <div className="row">
              <div className="col-md-4 mb-3">
                <h5>AI Assistant</h5>
                <p className="text-muted">AI-powered solutions for utilities and restaurants.</p>
              </div>
              <div className="col-md-2 mb-3">
                <h6>Product</h6>
                <ul className="list-unstyled">
                  <li><Link to="/features" className="text-muted text-decoration-none">Features</Link></li>
                  <li><Link to="/pricing" className="text-muted text-decoration-none">Pricing</Link></li>
                  <li><Link to="/api" className="text-muted text-decoration-none">API</Link></li>
                </ul>
              </div>
              <div className="col-md-2 mb-3">
                <h6>Company</h6>
                <ul className="list-unstyled">
                  <li><Link to="/about" className="text-muted text-decoration-none">About</Link></li>
                  <li><Link to="/blog" className="text-muted text-decoration-none">Blog</Link></li>
                  <li><Link to="/careers" className="text-muted text-decoration-none">Careers</Link></li>
                </ul>
              </div>
              <div className="col-md-4 mb-3">
                <h6>Contact</h6>
                <p className="text-muted">
                  <a href="mailto:contact@sitesmanager.co.uk" className="d-inline-flex align-items-center">
                    <i className="fas fa-envelope me-2"></i>
                    contact@sitesmanager.co.uk
                  </a>
                </p>
                <p>
                  <i className="fas fa-phone me-2"></i>0786 3207037
                </p>
              </div>
            </div>
            <div className="border-top pt-3 text-center text-muted">
              <small>&copy; {new Date().getFullYear()} AI Assistant. All rights reserved.</small>
            </div>
          </div>
        </footer>
      </>
    );
  };

  const WebLayout = ({ children }) => {
    return (
      <>



        {/* Dashboard Main Content */}
        <main className="">
          {children}
        </main>

      </>
    );
  };

  // Dashboard Layout Component
  const DashboardLayout = ({ children }) => {
    return (
      <>
        {/* Dashboard Navbar */}
        <nav className="navbar navbar-expand-lg navbar-dark bg-primary shadow">
          <div className="container-fluid">
            <Link className="navbar-brand" to="/dashboard">
              <i className="fas fa-users-cog me-2"></i>
              Business Management
            </Link>

            <button className="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#dashboardNav">
              <span className="navbar-toggler-icon"></span>
            </button>

            <div className="collapse navbar-collapse" id="dashboardNav">
              <ul className="navbar-nav me-auto">
                <li className="nav-item">
                  <Link className="nav-link" to="/dashboard">
                    <i className="fas fa-tachometer-alt me-1"></i> Dashboard
                  </Link>
                </li>
                <li className="nav-item">
                  <Link className="nav-link" to="/clients">
                    <i className="fas fa-address-book me-1"></i> Clients
                  </Link>
                </li>
                <li className="nav-item">
                  <Link className="nav-link" to="/orders">
                    <i className="fas fa-receipt me-1"></i> Orders
                  </Link>
                </li>
                <li className="nav-item">
                  <Link className="nav-link" to="/bookings">
                    <i className="fas fa-calendar-alt me-1"></i> Bookings
                  </Link>
                </li>
                <li className="nav-item">
                  <Link className="nav-link" to="/settings">
                    <i className="fas fa-cog me-1"></i> Settings
                  </Link>
                </li>
              </ul>

              <div className="d-flex align-items-center">
                <div className="text-light me-3 d-none d-md-block">
                  <i className="fas fa-building me-1"></i>
                  {user?.business_name || 'Loading...'}
                </div>

                <button
                  className="btn btn-outline-light me-3"
                  onClick={toggleTheme}
                  title={`Switch to ${theme === 'light' ? 'dark' : 'light'} theme`}
                >
                  <i className={`fas fa-${theme === 'light' ? 'moon' : 'sun'}`}></i>
                </button>

                <div className="dropdown">
                  <button
                    className="btn btn-outline-light dropdown-toggle"
                    type="button"
                    data-bs-toggle="dropdown"
                  >
                    <i className="fas fa-user-circle me-1"></i>
                    {user?.owner_name?.split(' ')[0] || 'User'}
                  </button>
                  <ul className="dropdown-menu dropdown-menu-end">
                    <li>
                      <Link className="dropdown-item" to="/profile">
                        <i className="fas fa-user me-2"></i>My Profile
                      </Link>
                    </li>
                    <li>
                      <Link className="dropdown-item" to="/settings">
                        <i className="fas fa-cog me-2"></i>Business Settings
                      </Link>
                    </li>
                    <li><hr className="dropdown-divider" /></li>
                    <li>
                      <button
                        className="dropdown-item"
                        onClick={handleLogoutClick}
                      >
                        <i className="fas fa-sign-out-alt me-2"></i>Logout
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </nav>

        {/* Dashboard Main Content */}
        <main className="container-fluid py-4">
          {children}
        </main>

        {/* Dashboard Footer */}
        <footer className="footer mt-auto py-3 bg-light border-top">
          <div className="container-fluid">
            <div className="row align-items-center">
              <div className="col-md-6">
                <span className="text-muted">
                  <i className="fas fa-code me-1"></i>
                  AI Assistant v1.0
                </span>
              </div>
              <div className="col-md-6 text-md-end">
                <span className="text-muted">
                  <i className="fas fa-server me-1"></i>
                  API: {window.location.hostname}:8080
                  <span className="mx-2">|</span>
                  <button
                    className="btn btn-sm btn-link text-muted p-0 text-decoration-none"
                    onClick={handleLogoutClick}
                  >
                    <i className="fas fa-sign-out-alt me-1"></i>
                    Logout
                  </button>
                </span>
              </div>
            </div>
          </div>
        </footer>
      </>
    );
  };

  // Show loading while checking auth state
  if (isLoading) {
    return (
      <div className="app-loading">
        <div className="d-flex justify-content-center align-items-center min-vh-100">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading application...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div className={`app-container ${theme}-theme`}>
        {/* Logout Confirmation Modal */}
        {showLogoutModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title poppin">Confirm Logout</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowLogoutModal(false)}
                >
                  ×
                </button>
              </div>
              <div className="modal-body poppin">
                <p>Are you sure you want to logout from the system?</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowLogoutModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleLogout}
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        )}

        <Routes>
          {/* Public Routes - No login required */}

          <Route path="/web/:slug" element={
            <WebLayout><WebBusinessPage /></WebLayout>

          } />

          <Route path="/" element={
            <PublicLayout>
              <HomePage />
            </PublicLayout>
          } />


          <Route path="/contact" element={
            <PublicLayout>
              <div className="container mt-4 py-5">
                <h1 style={{textAlign: "center"}}>Contact Us</h1>
                <ContactForm />
              </div>
            </PublicLayout>
          } />

          <Route path="/features" element={
            <PublicLayout>
              <div className="container mt-4 py-5">
                <h1>Features</h1>
                <p>Coming soon...</p>
              </div>
            </PublicLayout>
          } />

          <Route path="/pricing" element={
            <PublicLayout>
              <div className="container mt-4 py-5">
                <h1>Pricing</h1>
                <p>Coming soon...</p>
              </div>
            </PublicLayout>
          } />

          <Route path="/about" element={
            <PublicLayout>
              <div className="container mt-4 py-5">
                <h1>About Us</h1>
                <p>Coming soon...</p>
              </div>
            </PublicLayout>
          } />

          {/* Authentication Routes */}
          <Route path="/login" element={
            <PublicLayout>

              <div className="row justify-content-center">
                <div className="col-md-12">
                  <LoginForm onLoginSuccess={handleLoginSuccess} />
                </div>
              </div>

            </PublicLayout>
          } />

          <Route path="/register" element={
            <PublicLayout>
              <div className="container py-5">
                <div className="row justify-content-center">
                  <div className="col-md-6">
                    <div className="card">
                      <div className="card-body">
                        <h2 className="text-center mb-4 poppin">Register Business</h2>
                        <p className="text-center mb-4 poppin">
                          Already have an account? <Link to="/login">Login here</Link>
                        </p>
                        {/* Add registration form here */}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </PublicLayout>
          } />

     


   

      
      
     


          {/* Catch-all route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;